import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Mail, Send, Settings, History, CheckCircle, XCircle, AlertCircle,
  Loader2, TestTube, Eye, EyeOff
} from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';
import RichTextEditor from '../cms/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailManagement() {
  const [activeTab, setActiveTab] = useState('compose'); // 'compose', 'config', 'history'
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  
  // Config form state
  const [emailProvider, setEmailProvider] = useState('sendgrid');
  const [showPassword, setShowPassword] = useState(false);
  const [configForm, setConfigForm] = useState({
    sendgrid_api_key: '',
    sendgrid_from_email: '',
    sendgrid_from_name: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    smtp_from_email: '',
    smtp_from_name: ''
  });
  
  // Compose email state
  const [emailForm, setEmailForm] = useState({
    to_email: '',
    subject: '',
    body: ''
  });

  useEffect(() => {
    loadEmailConfig();
    if (activeTab === 'history') {
      loadEmailLogs();
    }
  }, [activeTab]);

  const loadEmailConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/email/config`);
      setConfig(response.data);
      setEmailProvider(response.data.email_provider);
      setConfigForm({
        sendgrid_api_key: response.data.sendgrid_api_key || '',
        sendgrid_from_email: response.data.sendgrid_from_email || '',
        sendgrid_from_name: response.data.sendgrid_from_name || '',
        smtp_host: response.data.smtp_host || '',
        smtp_port: response.data.smtp_port || 587,
        smtp_username: response.data.smtp_username || '',
        smtp_password: '',
        smtp_use_tls: response.data.smtp_use_tls !== false,
        smtp_from_email: response.data.smtp_from_email || '',
        smtp_from_name: response.data.smtp_from_name || ''
      });
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error loading email config:', error);
      }
    }
  };

  const loadEmailLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/email/logs`);
      setEmailLogs(response.data);
    } catch (error) {
      console.error('Error loading email logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const payload = {
        email_provider: emailProvider,
        ...configForm
      };

      if (config) {
        await axios.put(`${API_URL}/api/email/config`, payload);
        setMessage({ type: 'success', text: 'Email configuration updated successfully!' });
      } else {
        await axios.post(`${API_URL}/api/email/config`, payload);
        setMessage({ type: 'success', text: 'Email configuration created successfully!' });
      }

      await loadEmailConfig();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to save configuration'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const response = await axios.post(`${API_URL}/api/email/test-connection`);
      setMessage({ type: 'success', text: response.data.message });
      await loadEmailConfig();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Connection test failed'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    try {
      setLoading(true);
      setMessage(null);

      if (!emailForm.to_email || !emailForm.subject || !emailForm.body) {
        setMessage({ type: 'error', text: 'Please fill in all fields' });
        return;
      }

      await axios.post(`${API_URL}/api/email/send`, emailForm);
      setMessage({ type: 'success', text: 'Email sent successfully!' });
      
      // Clear form
      setEmailForm({
        to_email: '',
        subject: '',
        body: ''
      });
      
      // Reload logs if on history tab
      if (activeTab === 'history') {
        await loadEmailLogs();
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to send email'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderConfigTab = () => (
    <div className="space-y-6">
      {message && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Email Provider</CardTitle>
          <CardDescription>Choose your email sending method</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <button
              onClick={() => setEmailProvider('sendgrid')}
              className={`flex-1 p-4 border-2 rounded-lg transition ${
                emailProvider === 'sendgrid'
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-semibold mb-1">SendGrid</div>
              <div className="text-sm text-gray-600">Use SendGrid API for reliable email delivery</div>
            </button>
            <button
              onClick={() => setEmailProvider('smtp')}
              className={`flex-1 p-4 border-2 rounded-lg transition ${
                emailProvider === 'smtp'
                  ? 'border-teal-600 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-semibold mb-1">SMTP</div>
              <div className="text-sm text-gray-600">Connect your own email account (Gmail, Outlook, etc.)</div>
            </button>
          </div>
        </CardContent>
      </Card>

      {emailProvider === 'sendgrid' && (
        <Card>
          <CardHeader>
            <CardTitle>SendGrid Configuration</CardTitle>
            <CardDescription>Configure your SendGrid API credentials</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="sendgrid_api_key">API Key *</Label>
              <Input
                id="sendgrid_api_key"
                type="password"
                placeholder="SG.xxxxxxxxxxxxx"
                value={configForm.sendgrid_api_key}
                onChange={(e) => setConfigForm({ ...configForm, sendgrid_api_key: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="sendgrid_from_email">From Email *</Label>
              <Input
                id="sendgrid_from_email"
                type="email"
                placeholder="noreply@yourdomain.com"
                value={configForm.sendgrid_from_email}
                onChange={(e) => setConfigForm({ ...configForm, sendgrid_from_email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="sendgrid_from_name">From Name *</Label>
              <Input
                id="sendgrid_from_name"
                placeholder="Your Name or Company"
                value={configForm.sendgrid_from_name}
                onChange={(e) => setConfigForm({ ...configForm, sendgrid_from_name: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {emailProvider === 'smtp' && (
        <Card>
          <CardHeader>
            <CardTitle>SMTP Configuration</CardTitle>
            <CardDescription>Configure your SMTP server settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="smtp_host">SMTP Host *</Label>
                <Input
                  id="smtp_host"
                  placeholder="smtp.gmail.com"
                  value={configForm.smtp_host}
                  onChange={(e) => setConfigForm({ ...configForm, smtp_host: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Gmail: smtp.gmail.com, Outlook: smtp.office365.com
                </p>
              </div>
              <div>
                <Label htmlFor="smtp_port">SMTP Port *</Label>
                <Input
                  id="smtp_port"
                  type="number"
                  placeholder="587"
                  value={configForm.smtp_port}
                  onChange={(e) => setConfigForm({ ...configForm, smtp_port: parseInt(e.target.value) })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Usually 587 (TLS) or 465 (SSL)
                </p>
              </div>
            </div>
            <div>
              <Label htmlFor="smtp_username">Username / Email *</Label>
              <Input
                id="smtp_username"
                type="email"
                placeholder="your-email@gmail.com"
                value={configForm.smtp_username}
                onChange={(e) => setConfigForm({ ...configForm, smtp_username: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="smtp_password">Password / App Password *</Label>
              <div className="relative">
                <Input
                  id="smtp_password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Your password or app-specific password"
                  value={configForm.smtp_password}
                  onChange={(e) => setConfigForm({ ...configForm, smtp_password: e.target.value })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                For Gmail, use an <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">App Password</a>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="smtp_use_tls"
                checked={configForm.smtp_use_tls}
                onChange={(e) => setConfigForm({ ...configForm, smtp_use_tls: e.target.checked })}
                className="rounded border-gray-300"
              />
              <Label htmlFor="smtp_use_tls" className="cursor-pointer">Use TLS (recommended)</Label>
            </div>
            <div>
              <Label htmlFor="smtp_from_email">From Email *</Label>
              <Input
                id="smtp_from_email"
                type="email"
                placeholder="your-email@gmail.com"
                value={configForm.smtp_from_email}
                onChange={(e) => setConfigForm({ ...configForm, smtp_from_email: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="smtp_from_name">From Name *</Label>
              <Input
                id="smtp_from_name"
                placeholder="Your Name"
                value={configForm.smtp_from_name}
                onChange={(e) => setConfigForm({ ...configForm, smtp_from_name: e.target.value })}
              />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-3">
        <Button
          onClick={handleSaveConfig}
          disabled={loading}
          className="bg-teal-600 hover:bg-teal-700"
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Settings className="mr-2 h-4 w-4" />
              Save Configuration
            </>
          )}
        </Button>
        
        {config && (
          <Button
            onClick={handleTestConnection}
            disabled={loading}
            variant="outline"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <TestTube className="mr-2 h-4 w-4" />
                Test Connection
              </>
            )}
          </Button>
        )}
      </div>

      {config?.is_verified && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>
            Email configuration verified and working!
            {config.last_verified_at && ` Last verified: ${new Date(config.last_verified_at).toLocaleString()}`}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  const renderComposeTab = () => (
    <div className="space-y-6">
      {message && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      {!config && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please configure your email settings first in the Configuration tab.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Compose Email</CardTitle>
          <CardDescription>Send emails to patients, clinics, or team members</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="to_email">To *</Label>
            <Input
              id="to_email"
              type="email"
              placeholder="recipient@example.com"
              value={emailForm.to_email}
              onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              placeholder="Email subject"
              value={emailForm.subject}
              onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
            />
          </div>
          
          <div>
            <Label htmlFor="body">Message *</Label>
            <RichTextEditor
              content={emailForm.body}
              onChange={(html) => setEmailForm({ ...emailForm, body: html })}
              placeholder="Compose your email message..."
            />
          </div>

          <Button
            onClick={handleSendEmail}
            disabled={loading || !config}
            className="bg-teal-600 hover:bg-teal-700"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send Email
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const renderHistoryTab = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Email History</CardTitle>
          <CardDescription>View your sent emails and delivery status</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-600" />
              <p className="text-gray-600 mt-2">Loading email history...</p>
            </div>
          ) : emailLogs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Mail className="h-12 w-12 mx-auto mb-2 opacity-20" />
              <p>No emails sent yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {emailLogs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 transition"
                >
                  <div className="mt-1">
                    {log.status === 'sent' ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium">{log.subject}</div>
                    <div className="text-sm text-gray-600">To: {log.to_email}</div>
                    {log.error_message && (
                      <div className="text-sm text-red-600 mt-1">Error: {log.error_message}</div>
                    )}
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <div>{new Date(log.sent_at).toLocaleDateString()}</div>
                    <div>{new Date(log.sent_at).toLocaleTimeString()}</div>
                    <div className="text-xs mt-1 capitalize">{log.email_provider}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Email Management</h1>
        <p className="text-gray-600">Configure email settings and send messages</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b">
        <button
          onClick={() => setActiveTab('compose')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'compose'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Send className="inline h-4 w-4 mr-2" />
          Compose Email
        </button>
        <button
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'config'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Settings className="inline h-4 w-4 mr-2" />
          Configuration
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 font-medium transition ${
            activeTab === 'history'
              ? 'text-teal-600 border-b-2 border-teal-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <History className="inline h-4 w-4 mr-2" />
          History
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'compose' && renderComposeTab()}
      {activeTab === 'config' && renderConfigTab()}
      {activeTab === 'history' && renderHistoryTab()}
    </div>
  );
}
